import { useIsMutating, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getApiBaseUrl } from '@/lib/api-base-url';
import { authFetch } from '@/lib/auth-fetch';

// A StoneX FX broker trade the operator can attach as a contract reference.
// The tradeId becomes hedge_execution.contract_reference; decimals/dates arrive
// as strings from the backend to stay JSON-stable.
export interface FxTradeCandidate {
  tradeId: string;
  currencyPair: string | null;
  side: string | null;
  baseQuantity: string | null;
  absTradeValue: string | null;
  price: string | null;
  tradeDate: string | null;
  promptDate: string | null;
}

async function fetchFxTradeCandidates(currencyPair?: string): Promise<FxTradeCandidate[]> {
  const url = new URL(`${getApiBaseUrl()}/hedging/fx-trade-candidates`);
  if (currencyPair !== undefined && currencyPair !== '') {
    url.searchParams.set('currencyPair', currencyPair);
  }
  const response = await authFetch(url.toString());
  if (!response.ok) {
    throw new Error(`Request failed (${String(response.status)})`);
  }
  return (await response.json()) as FxTradeCandidate[];
}

export const HEDGING_FX_TRADE_CANDIDATES_QUERY_KEY = ['hedging', 'fx-trade-candidates'] as const;

// `enabled` lets callers defer the fetch until the picker is actually opened.
export function useFxTradeCandidates(currencyPair?: string, enabled = true) {
  return useQuery({
    queryKey: [...HEDGING_FX_TRADE_CANDIDATES_QUERY_KEY, currencyPair ?? null],
    queryFn: () => fetchFxTradeCandidates(currencyPair),
    enabled,
  });
}

// Resolve named broker trades. Position Matching needs a listed position's rate
// and value date, and must NOT use the candidates list for it: that one drops
// any trade already used as a contract reference (so a partially-allocated
// position's trade can never appear) and caps at the newest rows (#1485 review).
async function fetchFxTradesByReference(refs: string[]): Promise<FxTradeCandidate[]> {
  const url = new URL(`${getApiBaseUrl()}/hedging/fx-trades/by-reference`);
  url.searchParams.set('refs', refs.join(','));
  const response = await authFetch(url.toString());
  if (!response.ok) {
    throw new Error(`Request failed (${String(response.status)})`);
  }
  return (await response.json()) as FxTradeCandidate[];
}

export const HEDGING_FX_TRADES_BY_REF_QUERY_KEY = ['hedging', 'fx-trades', 'by-reference'] as const;

// `refs` is sorted into the key so the same set of positions reuses one cache
// entry regardless of the order the picker happened to list them in.
export function useFxTradesByReference(refs: string[]) {
  const sorted = [...refs].sort((a, b) => a.localeCompare(b));
  return useQuery({
    queryKey: [...HEDGING_FX_TRADES_BY_REF_QUERY_KEY, sorted.join(',')],
    queryFn: () => fetchFxTradesByReference(sorted),
    enabled: sorted.length > 0,
  });
}

export interface FxTradeSyncOutcome {
  fetched: number;
  upserted: number;
  inserted: number;
  updated: number;
}

// Pulls the broker feed for the default rolling window and nothing else. The
// candidate list only refreshes on a sync, so a trade executed minutes ago is
// otherwise unreachable until the nightly job runs.
// Exported so Position Matching's refresh can pull the same feed through the
// same call rather than keeping a second copy of the request in sync.
export async function syncFxTrades(): Promise<FxTradeSyncOutcome> {
  const response = await authFetch(`${getApiBaseUrl()}/hedging/fx-trades/sync`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({}),
  });
  if (!response.ok) {
    throw new Error(`Fetching broker trades failed (${String(response.status)})`);
  }
  return (await response.json()) as FxTradeSyncOutcome;
}

// Shared key so every Fetch button on screen sees the same in-flight sync and
// disables itself — two clicks would hit StoneX twice for the same window.
export const FX_TRADE_SYNC_MUTATION_KEY = ['hedging', 'fx-trades', 'sync'] as const;

export function useIsFxTradeSyncRunning(): boolean {
  return useIsMutating({ mutationKey: FX_TRADE_SYNC_MUTATION_KEY }) > 0;
}

export function useFxTradeSync() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: FX_TRADE_SYNC_MUTATION_KEY,
    mutationFn: syncFxTrades,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: HEDGING_FX_TRADE_CANDIDATES_QUERY_KEY });
    },
  });
}
